import React from 'react'
import { Form, Spin, Switch } from 'antd'
import { useRequest } from 'ahooks'
import Button from '@/components/button/button'
import $msg from '@/utils/message'
import Auth from '@/views/main/components/auth/auth'
import { getExperienceRules, updateExperienceRules } from '../api'
import SettingPage, {
    SettingActions,
    SettingGrid,
    SettingInputNumber,
    SettingPanel,
    SettingPreview,
} from './setting-page'
import { useMemberSettingAccess } from './setting-access'

interface GrowthRuleForm {
    recharge_status: boolean
    recharge_multiple?: number
    consume_status: boolean
    consume_multiple?: number
}

const GrowthRuleSetting: React.FC = () => {
    const [form] = Form.useForm<GrowthRuleForm>()
    const access = useMemberSettingAccess()
    const loadReq = useRequest(getExperienceRules, {
        onSuccess: rules => {
            const rechargeRule = (rules || []).find((rule: { type: number }) => rule.type === 0)
            const consumeRule = (rules || []).find((rule: { type: number }) => rule.type === 1)
            form.setFieldsValue({
                recharge_status: !!rechargeRule?.status,
                recharge_multiple: Number(rechargeRule?.multiple || 0),
                consume_status: !!consumeRule?.status,
                consume_multiple: Number(consumeRule?.multiple || 0),
            })
        },
        onError: e => $msg('error', e.message || '获取成长值规则失败'),
    })
    const saveReq = useRequest(
        async (values: GrowthRuleForm) =>
            updateExperienceRules({
                rules: [
                    {
                        type: 0,
                        status: values.recharge_status ? 1 : 0,
                        multiple: Number(values.recharge_multiple || 0),
                    },
                    { type: 1, status: values.consume_status ? 1 : 0, multiple: Number(values.consume_multiple || 0) },
                ],
            }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '保存成功')
                loadReq.refresh()
            },
            onError: e => $msg('error', e.message || '保存成长值规则失败'),
        },
    )

    const onSubmit = async () => {
        const values = await form.validateFields()
        if (values.recharge_status && Number(values.recharge_multiple || 0) <= 0) {
            $msg('warning', '请填写每充值1元增加的成长值')

            return
        }
        if (values.consume_status && Number(values.consume_multiple || 0) <= 0) {
            $msg('warning', '请填写每消费1元增加的成长值')

            return
        }
        saveReq.run(values)
    }

    return (
        <SettingPage title="成长值规则" readOnly={access.readOnly}>
            <Spin spinning={loadReq.loading}>
                <Form form={form} layout="vertical">
                    <SettingGrid>
                        <SettingPanel title="成长值获取" desc="配置会员通过消费和充值获得成长值的规则。">
                            <Form.Item noStyle shouldUpdate>
                                {() => (
                                    <SettingPreview
                                        items={[
                                            {
                                                label: '消费成长值',
                                                value: form.getFieldValue('consume_status')
                                                    ? `每消费1元 +${form.getFieldValue('consume_multiple') || 0}`
                                                    : '已关闭',
                                            },
                                            {
                                                label: '充值成长值',
                                                value: form.getFieldValue('recharge_status')
                                                    ? `每充值1元 +${form.getFieldValue('recharge_multiple') || 0}`
                                                    : '已关闭',
                                            },
                                        ]}
                                    />
                                )}
                            </Form.Item>
                            <Form.Item name="consume_status" label="消费获得成长值" valuePropName="checked">
                                <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                            </Form.Item>
                            <Form.Item name="consume_multiple" label="每消费1元增加">
                                <SettingInputNumber min={0} precision={0} addonAfter="成长值" />
                            </Form.Item>
                            <Form.Item name="recharge_status" label="充值获得成长值" valuePropName="checked">
                                <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                            </Form.Item>
                            <Form.Item name="recharge_multiple" label="每充值1元增加">
                                <SettingInputNumber min={0} precision={0} addonAfter="成长值" />
                            </Form.Item>
                            <SettingActions>
                                <Auth perssion={20} onClick={onSubmit}>
                                    <Button types="default" loading={saveReq.loading} disabled={saveReq.loading}>
                                        保存
                                    </Button>
                                </Auth>
                            </SettingActions>
                        </SettingPanel>
                        <SettingPanel title="规则说明" desc="成长值用于计算会员等级，关闭规则不会清除会员已有成长值。">
                            <div className="member-config-page__notice">
                                修改获取倍数只影响保存后的新消费和新充值记录；会员已有成长值以及历史等级变更记录不会重新计算。
                            </div>
                        </SettingPanel>
                    </SettingGrid>
                </Form>
            </Spin>
        </SettingPage>
    )
}

export default GrowthRuleSetting
